import React, { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "react-toastify";

// MUI
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import TablePagination from "@mui/material/TablePagination";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import RepeatIcon from "@mui/icons-material/Repeat";
import { formatName } from "../../../helpers/helpers";
import ImageIcon from "@mui/icons-material/Image";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloseIcon from "@mui/icons-material/Close";
import HandymanIcon from "@mui/icons-material/Handyman";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { Lightbox } from "react-modal-image";

// App
import {
  getRightworkAPI,
  postRightworkAPI,
} from "../requests/utilities/requests";
import {
  API_get_maintenance_requests,
  API_post_maintenance_request,
  API_update_maintenance_request,
  API_delete_maintenance_request,
  API_get_store_employees_for_assignment,
  API_get_recurring_maintenance_templates,
  API_post_recurring_maintenance_template,
  API_decide_maintenance_request_approval,
} from "../../../constants";
import ComputerIcon from "@mui/icons-material/Computer";
import CampaignIcon from "@mui/icons-material/Campaign";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import {
  StatusBadge,
  resolveStatusLabel,
  PriorityBadge,
  IssueTypeBadge,
  CATEGORY_CONFIG,
  getDefaultStatusOptions,
  openStatusOptions,
} from "./MaintenanceBadges";
import MaintenanceTicketDrawer from "./MaintenanceTicketDrawer";
import useTicketOptions from "./useTicketOptions";
import MaintenanceHistoryTooltip from "./MaintenanceHistoryTooltip";
import MaintenanceCommentTooltip from "./MaintenanceCommentTooltip";
import RecurringManagementDrawer from "./RecurringManagementDrawer";
import TicketAssignmentSettingsModal from "./TicketAssignmentSettingsModal";
import OperationsPageHeader from "../home/top_bar_components/OperationsPageHeader";
import SubTabs from "../home/top_bar_components/SubTabs";
import AssetsView from "./assets/AssetsView";

const VIDEO_EXTENSIONS = new Set(["mp4", "mov", "webm", "m4v", "avi", "hevc"]);
// HEIC/HEIF are accepted for upload but the backend re-encodes them to JPEG
// (browsers other than Safari can't render them); until that runs they show
// as a generic file rather than a broken image.
const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "webp", "svg"]);

function getFileExtension(file) {
  if (file.extension) return file.extension.toLowerCase();
  const source = file.name || file.presigned_url || "";
  return source.split('.').pop()?.split('?')[0]?.toLowerCase() || "";
}

function getFileKind(file) {
  const ext = getFileExtension(file);
  if (VIDEO_EXTENSIONS.has(ext)) return "video";
  if (IMAGE_EXTENSIONS.has(ext)) return "image";
  if (ext === "pdf") return "pdf";
  return "other";
}

const selectSx = {
  backgroundColor: "white",
  borderRadius: "8px",
  height: "32px",
  fontSize: "13px",
  fontWeight: 500,
  color: "#334155",
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#6d28d9",
  },
};


// Per-category store flag and the override that opens it to non-managers.
const CATEGORY_STORE_FLAG = {
  maintenance: "maintenance_enabled",
  it: "it_tickets_enabled",
  marketing: "marketing_tickets_enabled",
  menu: "menu_tickets_enabled",
};
const CATEGORY_OVERRIDE = {
  maintenance: "maintenance_access",
  it: "it_access",
  marketing: "marketing_access",
  menu: "menu_access",
};
const MANAGER_ROLES = ["company_manager", "company_admin", "rightwork_internal"];
// Width the Asset column adds to the tickets table when it shows; longer
// asset names truncate with the full name on hover.
const ASSET_COLUMN_WIDTH = 200;

const CATEGORY_ICON_MAP = {
  maintenance: HandymanIcon,
  it: ComputerIcon,
  marketing: CampaignIcon,
  menu: RestaurantMenuIcon,
};

function MaintenanceDisplay(props) {
  const {
    homeStore: propsHomeStore,
    homeCompany: propsHomeCompany,
    storeNames,
    storeOptions,
  } = props;

  // Company scope follows the shared header's company selector, which
  // switches the home store into the chosen company (preferring a
  // manager/admin membership there); the backend derives company scope and
  // the user's role from this store_id.
  const selectedCompanyId = propsHomeCompany;
  const effectiveHomeStore = propsHomeStore;

  // Re-derive role and overrides from the anchor store's membership
  const anchorMembership = useMemo(
    () => (storeOptions || []).find((m) => m.store === effectiveHomeStore),
    [storeOptions, effectiveHomeStore]
  );

  const permissionRole = anchorMembership?.permission_type || "company_employee";
  const permissionOverrides = anchorMembership?.permission_type_overrides || [];

  // Category feature flags are per-store, but the backend treats managers/
  // admins as having company-wide visibility. Surface a category tab when
  // any store in the selected company has it enabled.
  const storesInSelectedCompany = useMemo(
    () => (storeNames || []).filter((s) => s.company === selectedCompanyId),
    [storeNames, selectedCompanyId]
  );
  const maintenanceEnabled = storesInSelectedCompany.some((s) => s.maintenance_enabled);
  const itTicketsEnabled = storesInSelectedCompany.some((s) => s.it_tickets_enabled);
  const marketingTicketsEnabled = storesInSelectedCompany.some((s) => s.marketing_tickets_enabled);
  const menuTicketsEnabled = storesInSelectedCompany.some((s) => s.menu_tickets_enabled);

  const homeStore = effectiveHomeStore;
  const homeCompany = selectedCompanyId;

  const isManager = ["company_manager", "company_admin", "rightwork_internal"].includes(permissionRole);
  const isAdmin = ["company_admin", "rightwork_internal"].includes(permissionRole);
  const hasAnyTicketAccess = permissionOverrides?.some((o) =>
    ["maintenance_access", "it_access", "marketing_access", "menu_access"].includes(o)
  );
  const canManageMaintenance = isManager || hasAnyTicketAccess;

  // Compute which ticket categories this user can access
  const accessibleCategories = useMemo(() => {
    const cats = [];
    if (maintenanceEnabled && (isManager || permissionOverrides?.includes("maintenance_access")))
      cats.push({ value: "maintenance", label: "Maintenance", icon: HandymanIcon });
    if (itTicketsEnabled && (isManager || permissionOverrides?.includes("it_access")))
      cats.push({ value: "it", label: "IT", icon: ComputerIcon });
    if (marketingTicketsEnabled && (isManager || permissionOverrides?.includes("marketing_access")))
      cats.push({ value: "marketing", label: "Marketing", icon: CampaignIcon });
    if (menuTicketsEnabled && (isManager || permissionOverrides?.includes("menu_access")))
      cats.push({ value: "menu", label: "Menu", icon: RestaurantMenuIcon });
    return cats;
  }, [maintenanceEnabled, itTicketsEnabled, marketingTicketsEnabled, menuTicketsEnabled, isManager, permissionOverrides]);

  const isMultiCategory = accessibleCategories.length > 1;
  const [activeCategory, setActiveCategory] = useState(() => {
    if (maintenanceEnabled && (isManager || permissionOverrides?.includes("maintenance_access"))) return "maintenance";
    if (itTicketsEnabled && (isManager || permissionOverrides?.includes("it_access"))) return "it";
    if (marketingTicketsEnabled && (isManager || permissionOverrides?.includes("marketing_access"))) return "marketing";
    if (menuTicketsEnabled && (isManager || permissionOverrides?.includes("menu_access"))) return "menu";
    return "maintenance";
  });

  // Reset activeCategory when accessible categories change (e.g. store switch)
  useEffect(() => {
    if (accessibleCategories.length > 0 && !accessibleCategories.some((c) => c.value === activeCategory)) {
      setActiveCategory(accessibleCategories[0].value);
    }
  }, [accessibleCategories, activeCategory]);

  // Issue types and priority labels for this store and category (companies
  // can relabel both). Drawers and the priority filter stay read-only while
  // the status is "loading" or "error". Shared with the Tasks hub, which
  // raises tickets from failed audit items.
  const {
    issueTypeOptions,
    priorityOptions,
    statusOptions,
    menuOptions,
    ticketOptionsStatus,
    retryTicketOptions,
  } = useTicketOptions(homeStore, activeCategory);
  const priorityLabelFor = (value) => priorityOptions.find((o) => o.value === value)?.label;
  // Status wording is per category (menu tickets relabel statuses and hide
  // Waiting: Vendor); the stored values never change.
  const statusLabelFor = (value) => statusOptions.find((o) => o.value === value)?.label;
  const openStatusValues = useMemo(
    () => openStatusOptions(statusOptions).map((o) => o.value),
    [statusOptions]
  );


  // Data state
  const [maintenanceRequests, setMaintenanceRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [approvers, setApprovers] = useState([]);

  // Filter state
  const [statusFilter, setStatusFilter] = useState(() =>
    openStatusOptions(getDefaultStatusOptions(activeCategory)).map((o) => o.value)
  );
  // Each category has its own status set, so switching categories resets the
  // status filter to that category's open statuses.
  useEffect(() => {
    setStatusFilter(openStatusOptions(getDefaultStatusOptions(activeCategory)).map((o) => o.value));
  }, [activeCategory]);
  const [priorityFilter, setPriorityFilter] = useState([]);
  const [issueTypeFilter, setIssueTypeFilter] = useState([]);
  const [storeFilter, setStoreFilter] = useState([]);
  const [assignedToFilter, setAssignedToFilter] = useState([]);
  const [submitterFilter, setSubmitterFilter] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Table state
  const [orderBy, setOrderBy] = useState("created_at");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Recurring management state
  const [recurringDrawerOpen, setRecurringDrawerOpen] = useState(false);
  const [recurringTemplates, setRecurringTemplates] = useState([]);
  const [recurringTemplatesLoading, setRecurringTemplatesLoading] = useState(false);

  // Assignment settings modal
  const [assignmentSettingsOpen, setAssignmentSettingsOpen] = useState(false);

  // Tickets | Assets toggle (maintenance, managers only). Assets are managed
  // per store, at stores where the user is a manager and maintenance is on.
  const [view, setView] = useState("tickets");
  const [drawerInitialAsset, setDrawerInitialAsset] = useState(null);
  // Bumped after ticket saves so asset rollups refresh.
  const [assetRefreshKey, setAssetRefreshKey] = useState(0);
  const showAssetsToggle = activeCategory === "maintenance" && isManager;
  useEffect(() => {
    if (!showAssetsToggle) setView("tickets");
  }, [showAssetsToggle]);

  // Lightbox state
  const [selectedImage, setSelectedImage] = useState(null);
  const [showLightbox, setShowLightbox] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);

  const availableStores = useMemo(() => {
    return (storeNames || []).filter((s) => s.company === homeCompany);
  }, [storeNames, homeCompany]);

  // Stores a new ticket in the active category can be raised at: the
  // category is on there, and the user's membership at that store is a
  // manager role or holds the category's override (the backend checks the
  // same membership per store on each create).
  const createStores = useMemo(() => {
    const flag = CATEGORY_STORE_FLAG[activeCategory];
    const override = CATEGORY_OVERRIDE[activeCategory];
    return availableStores.filter((s) => {
      if (flag && !s[flag]) return false;
      const membership = (storeOptions || []).find((m) => m.store === s.id);
      if (!membership) return false;
      return (
        MANAGER_ROLES.includes(membership.permission_type) ||
        (membership.permission_type_overrides || []).includes(override)
      );
    });
  }, [availableStores, storeOptions, activeCategory]);

  const assetStores = useMemo(
    () =>
      availableStores.filter((s) => {
        if (!s.maintenance_enabled) return false;
        const membership = (storeOptions || []).find((m) => m.store === s.id);
        return MANAGER_ROLES.includes(membership?.permission_type);
      }),
    [availableStores, storeOptions]
  );

  // Fetch maintenance requests
  // Live mirror of the (store, category) a fetch was issued for: the header
  // company selector can change the store mid-flight and getRightworkAPI
  // neither cancels nor tags requests, so a slower response for the
  // previous company must not populate the new company's page.
  const requestScopeRef = React.useRef({ homeStore, activeCategory });
  useEffect(() => {
    requestScopeRef.current = { homeStore, activeCategory };
  }, [homeStore, activeCategory]);

  const fetchMaintenanceRequests = useCallback(async (showLoading = true) => {
    const requestStore = homeStore;
    const requestCategory = activeCategory;
    const isCurrent = () =>
      requestScopeRef.current.homeStore === requestStore &&
      requestScopeRef.current.activeCategory === requestCategory;
    if (showLoading) setIsLoading(true);
    try {
      const params = { store_id: requestStore, category: requestCategory };
      const res = await getRightworkAPI(API_get_maintenance_requests, params);
      if (!isCurrent()) return;
      const data = res.data?.redux?.payload?.maintenance_requests || [];
      setMaintenanceRequests(data);
    } catch (error) {
      if (!isCurrent()) return;
      toast.error("Failed to load maintenance requests");
    } finally {
      if (showLoading && isCurrent()) setIsLoading(false);
    }
  }, [homeStore, activeCategory]);

  // Fetch employees for the Assigned To dropdown
  const fetchEmployees = useCallback(async (ticketStoreId) => {
    if (!canManageMaintenance) return;
    const storeIdForQuery = ticketStoreId || homeStore;
    try {
      const params = { store_id: homeStore, ticket_store_id: storeIdForQuery, category: activeCategory };
      const res = await getRightworkAPI(API_get_store_employees_for_assignment, params);
      setEmployees(res.data?.redux?.payload?.employees || []);
    } catch (error) {
      // Silently fail - employees list is non-critical
    }
  }, [homeStore, activeCategory, canManageMaintenance]);

  // Fetch the approver pool for the Approver dropdown (role=approver)
  const fetchApprovers = useCallback(async (ticketStoreId) => {
    if (!canManageMaintenance) return;
    const storeIdForQuery = ticketStoreId || homeStore;
    try {
      const params = {
        store_id: homeStore,
        ticket_store_id: storeIdForQuery,
        category: activeCategory,
        role: "approver",
      };
      const res = await getRightworkAPI(API_get_store_employees_for_assignment, params);
      setApprovers(res.data?.redux?.payload?.employees || []);
    } catch (error) {
      // Silently fail - approver list is non-critical
    }
  }, [homeStore, activeCategory, canManageMaintenance]);

  // Fetch both the assignee and approver pools for a ticket's store
  const fetchTicketPeople = useCallback((ticketStoreId) => {
    fetchEmployees(ticketStoreId);
    fetchApprovers(ticketStoreId);
  }, [fetchEmployees, fetchApprovers]);

  useEffect(() => {
    fetchMaintenanceRequests();
    fetchTicketPeople();
  }, [fetchMaintenanceRequests, fetchTicketPeople]);

  // Re-fetch tickets when an assistant action confirms a ticket
  // mutation. ``opsRefreshSignal`` is bumped from MainDisplay's
  // handleOpsActionConfirmed when ``create_ticket`` /
  // ``edit_ticket`` / ``comment_on_ticket`` confirms; gate on the
  // timestamp so we don't double-fire under React strict mode.
  const lastOpsSignalTimestampRef = React.useRef(null);
  useEffect(() => {
    const ts = props.opsRefreshSignal?.timestamp;
    if (!ts || ts === lastOpsSignalTimestampRef.current) return;
    lastOpsSignalTimestampRef.current = ts;
    if (!props.opsRefreshSignal?.types?.includes("tickets")) return;
    fetchMaintenanceRequests(false);
  }, [props.opsRefreshSignal, fetchMaintenanceRequests]);

  // Fetch recurring templates
  const fetchRecurringTemplates = useCallback(async () => {
    setRecurringTemplatesLoading(true);
    try {
      const params = { store_id: homeStore, category: activeCategory };
      const res = await getRightworkAPI(API_get_recurring_maintenance_templates, params);
      setRecurringTemplates(res.data?.redux?.payload?.recurring_maintenance_templates || []);
    } catch (error) {
      toast.error("Failed to load recurring templates");
    } finally {
      setRecurringTemplatesLoading(false);
    }
  }, [homeStore, activeCategory]);

  // Clear person filters on category/store change
  useEffect(() => {
    setAssignedToFilter([]);
    setSubmitterFilter([]);
  }, [activeCategory, homeStore]);

  // Reset filters/pagination and close any open drawer when the active
  // company changes, so a ticket from the previous company can't be saved
  // or deleted against the new company's anchor store.
  useEffect(() => {
    setStoreFilter([]);
    setPage(0);
    setDrawerOpen(false);
    setSelectedTicket(null);
    setRecurringDrawerOpen(false);
    setView("tickets");
  }, [homeCompany]);

  // Handlers
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleCreateTicket = async (formData) => {
    // Check if this is a recurring template submission
    if (formData.recurrence_interval) {
      return handleCreateRecurringTemplate(formData);
    }

    const { store_ids, ...ticketData } = formData;
    const storeIdsToCreate = store_ids?.length ? store_ids : [homeStore];
    // One request raised for several stores makes one ticket per store; a
    // shared id keeps them tied together.
    if (storeIdsToCreate.length > 1 && window.crypto?.randomUUID) {
      ticketData.request_group_id = window.crypto.randomUUID();
    }
    const toastId = toast.loading(
      storeIdsToCreate.length > 1
        ? `Creating ${storeIdsToCreate.length} tickets...`
        : "Creating ticket..."
    );
    const results = await Promise.allSettled(
      storeIdsToCreate.map((storeId) =>
        postRightworkAPI(API_post_maintenance_request, {
          store_id: storeId,
          ...ticketData,
        })
      )
    );
    toast.dismiss(toastId);
    const failedCount = results.filter((r) => r.status === "rejected").length;
    const succeededCount = results.length - failedCount;
    if (failedCount === 0) {
      toast.success(
        succeededCount > 1
          ? `${succeededCount} tickets created`
          : "Ticket created"
      );
    } else if (succeededCount === 0) {
      toast.error("Failed to create tickets");
      throw results.find((r) => r.status === "rejected").reason;
    } else {
      toast.error(
        `${succeededCount} ticket(s) created, ${failedCount} failed`
      );
    }
    fetchMaintenanceRequests(false);
    setAssetRefreshKey((n) => n + 1);
  };

  const handleCreateRecurringTemplate = async (formData) => {
    const { store_ids, ...templateData } = formData;
    const storeIdsToCreate = store_ids?.length ? store_ids : [homeStore];
    const toastId = toast.loading(
      storeIdsToCreate.length > 1
        ? `Creating ${storeIdsToCreate.length} recurring tickets...`
        : "Creating recurring ticket..."
    );
    const results = await Promise.allSettled(
      storeIdsToCreate.map((storeId) =>
        postRightworkAPI(API_post_recurring_maintenance_template, {
          store_id: storeId,
          ...templateData,
        })
      )
    );
    toast.dismiss(toastId);
    const failedCount = results.filter((r) => r.status === "rejected").length;
    const succeededCount = results.length - failedCount;
    if (failedCount === 0) {
      toast.success(
        succeededCount > 1
          ? `${succeededCount} recurring tickets created`
          : "Recurring ticket created"
      );
      fetchMaintenanceRequests();
    } else if (succeededCount === 0) {
      toast.error("Failed to create recurring tickets");
      throw results.find((r) => r.status === "rejected").reason;
    } else {
      toast.error(
        `${succeededCount} recurring ticket(s) created, ${failedCount} failed`
      );
    }
  };

  const handleUpdateTicket = async (formData) => {
    const { store_ids, ...ticketData } = formData;
    const toastId = toast.loading("Updating ticket...");
    try {
      await postRightworkAPI(API_update_maintenance_request, {
        store_id: homeStore,
        new_store_id: store_ids?.[0] || undefined,
        ...ticketData,
      });
      toast.dismiss(toastId);
      toast.success("Ticket updated");
      fetchMaintenanceRequests(false);
      setAssetRefreshKey((n) => n + 1);
    } catch (error) {
      toast.dismiss(toastId);
      throw error;
    }
  };

  const handleDeleteTicket = async (uuid) => {
    const toastId = toast.loading("Archiving ticket...");
    try {
      await postRightworkAPI(API_delete_maintenance_request, {
        store_id: homeStore,
        maintenance_request_uuid: uuid,
      });
      toast.dismiss(toastId);
      toast.success("Ticket archived");
      fetchMaintenanceRequests(false);
      setAssetRefreshKey((n) => n + 1);
    } catch (error) {
      toast.dismiss(toastId);
      toast.error("Failed to archive ticket");
      throw error;
    }
  };

  const [initialTab, setInitialTab] = useState("details");

  const handleRowClick = (ticket, tab = "details") => {
    setSelectedTicket(ticket);
    setInitialTab(tab);
    setDrawerOpen(true);
    fetchTicketPeople(ticket.store?.id);
  };

  const handleNewTicket = () => {
    setSelectedTicket(null);
    setDrawerInitialAsset(null);
    setDrawerOpen(true);
    fetchTicketPeople();
  };

  // "New ticket for this asset" from an asset's page.
  const handleNewTicketForAsset = (asset) => {
    setSelectedTicket(null);
    setDrawerInitialAsset(asset);
    setDrawerOpen(true);
    fetchTicketPeople(asset.store?.id);
  };

  const handleDecideApproval = async (uuid, decision, note) => {
    const toastId = toast.loading(
      decision === "approve" ? "Approving ticket..." : "Rejecting ticket..."
    );
    try {
      await postRightworkAPI(API_decide_maintenance_request_approval, {
        store_id: homeStore,
        maintenance_request_uuid: uuid,
        decision,
        note: note || "",
      });
      toast.dismiss(toastId);
      toast.success(decision === "approve" ? "Ticket approved" : "Ticket rejected");
      fetchMaintenanceRequests(false);
    } catch (error) {
      toast.dismiss(toastId);
      throw error;
    }
  };

  const handleMultiSelectChange = (setter, allOptions) => (event) => {
    const { value } = event.target;
    if (value.includes("all")) {
      setter((prev) =>
        prev.length === allOptions.length ? [] : allOptions.map((o) => o.value)
      );
    } else {
      setter(value);
    }
    setPage(0);
  };

  // Unique assignees and submitters for filter dropdowns.
  // Dedupe by user_id so a person with StoreTeamMember records in multiple
  // stores only appears once. STMs without a linked user (e.g. from CSV
  // imports) fall back to the STM id so they still appear.
  const personKey = (person) => {
    if (!person) return null;
    if (person.user_id) return `u:${person.user_id}`;
    if (person.id) return `s:${person.id}`;
    return null;
  };

  const availableAssignees = useMemo(() => {
    const map = new Map();
    maintenanceRequests.forEach((t) => {
      (t.assignees || []).forEach((person) => {
        const key = personKey(person);
        if (key && !map.has(key)) {
          map.set(key, person.full_name || "");
        }
      });
    });
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [maintenanceRequests]);

  const availableSubmitters = useMemo(() => {
    const map = new Map();
    maintenanceRequests.forEach((t) => {
      const key = personKey(t.submitter);
      if (key && !map.has(key)) {
        map.set(key, t.submitter.full_name || "");
      }
    });
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [maintenanceRequests]);

  // Prune stale IDs from person filters when available options change
  useEffect(() => {
    const validIds = new Set(availableAssignees.map((a) => a.id));
    setAssignedToFilter((prev) => {
      const pruned = prev.filter((id) => validIds.has(id));
      return pruned.length === prev.length ? prev : pruned;
    });
  }, [availableAssignees]);

  useEffect(() => {
    const validIds = new Set(availableSubmitters.map((s) => s.id));
    setSubmitterFilter((prev) => {
      const pruned = prev.filter((id) => validIds.has(id));
      return pruned.length === prev.length ? prev : pruned;
    });
  }, [availableSubmitters]);

  // Status filter options: the category's statuses, plus any status the
  // category hides (e.g. waiting_vendor on a menu ticket) that a loaded
  // ticket is actually on, so every ticket stays reachable and filterable.
  const filterStatusOptions = useMemo(() => {
    const known = new Set(statusOptions.map((o) => o.value));
    const extra = [];
    maintenanceRequests.forEach((t) => {
      if (t.status && !known.has(t.status)) {
        known.add(t.status);
        extra.push({ value: t.status, label: resolveStatusLabel(t.status) });
      }
    });
    return [...statusOptions, ...extra];
  }, [statusOptions, maintenanceRequests]);

  // Filtering
  const filteredData = useMemo(() => {
    let data = [...maintenanceRequests];

    if (statusFilter.length > 0) {
      // The default "Open" view also keeps open tickets on a status the
      // category hides (e.g. waiting_vendor on a menu ticket); picking
      // specific statuses filters strictly.
      const isOpenView =
        statusFilter.length === openStatusValues.length &&
        openStatusValues.every((v) => statusFilter.includes(v));
      const known = new Set(statusOptions.map((o) => o.value));
      data = data.filter(
        (t) =>
          statusFilter.includes(t.status) ||
          (isOpenView && !known.has(t.status) && !["completed", "closed"].includes(t.status))
      );
    }
    if (priorityFilter.length > 0) {
      data = data.filter((t) => priorityFilter.includes(t.priority));
    }
    if (issueTypeFilter.length > 0) {
      data = data.filter((t) => issueTypeFilter.includes(t.issue_type));
    }
    if (storeFilter.length > 0) {
      data = data.filter((t) => storeFilter.includes(t.store?.id));
    }
    if (assignedToFilter.length > 0) {
      data = data.filter((t) =>
        (t.assignees || []).some((p) => assignedToFilter.includes(personKey(p)))
      );
    }
    if (submitterFilter.length > 0) {
      data = data.filter((t) => submitterFilter.includes(personKey(t.submitter)));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const qNum = q.replace(/^#/, "");
      data = data.filter(
        (t) =>
          t.title?.toLowerCase().includes(q) ||
          t.description?.toLowerCase().includes(q) ||
          t.location_detail?.toLowerCase().includes(q) ||
          t.asset?.name?.toLowerCase().includes(q) ||
          t.asset?.code?.toLowerCase() === q ||
          t.submitter?.full_name?.toLowerCase().includes(q) ||
          (t.ticket_number != null && String(t.ticket_number) === qNum)
      );
    }

    return data;
  }, [maintenanceRequests, statusFilter, openStatusValues, statusOptions, priorityFilter, issueTypeFilter, storeFilter, assignedToFilter, submitterFilter, searchQuery]);

  // Sorting
  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      let aVal, bVal;
      switch (orderBy) {
        case "ticket_number":
          aVal = a.ticket_number ?? -1;
          bVal = b.ticket_number ?? -1;
          break;
        case "title":
          aVal = a.title?.toLowerCase() || "";
          bVal = b.title?.toLowerCase() || "";
          break;
        case "store":
          aVal = a.store?.name?.toLowerCase() || "";
          bVal = b.store?.name?.toLowerCase() || "";
          break;
        case "issue_type":
          aVal = a.issue_type || "";
          bVal = b.issue_type || "";
          break;
        case "priority":
          const priorityOrder = { urgent: 0, high: 1, normal: 2, low: 3 };
          aVal = priorityOrder[a.priority] ?? 4;
          bVal = priorityOrder[b.priority] ?? 4;
          break;
        case "status":
          aVal = resolveStatusLabel(a.status, statusLabelFor(a.status));
          bVal = resolveStatusLabel(b.status, statusLabelFor(b.status));
          break;
        case "assigned_to":
          aVal = (a.assignees || []).map((p) => p.full_name).join(", ").toLowerCase();
          bVal = (b.assignees || []).map((p) => p.full_name).join(", ").toLowerCase();
          break;
        case "submitter":
          aVal = a.submitter?.full_name?.toLowerCase() || "";
          bVal = b.submitter?.full_name?.toLowerCase() || "";
          break;
        case "date_reported":
          aVal = a.date_reported || "";
          bVal = b.date_reported || "";
          break;
        case "date_resolved":
          aVal = a.date_resolved || "";
          bVal = b.date_resolved || "";
          break;
        case "asset":
          aVal = a.asset?.name?.toLowerCase() || "";
          bVal = b.asset?.name?.toLowerCase() || "";
          break;
        case "go_live_date":
          aVal = a.menu_details?.go_live_date || "";
          bVal = b.menu_details?.go_live_date || "";
          break;
        default: // created_at
          aVal = a.created_at || "";
          bVal = b.created_at || "";
      }
      if (aVal < bVal) return order === "asc" ? -1 : 1;
      if (aVal > bVal) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, order, orderBy, statusOptions]);

  // Pagination
  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  const renderMultiSelectValue = (selected, options, label) => {
    if (selected.length === 0 || selected.length === options.length) return `All ${label}`;
    const names = options
      .filter((o) => selected.includes(o.value))
      .map((o) => o.label);
    if (names.length <= 2) return names.join(", ");
    return `${names[0]}, +${names.length - 1} more`;
  };

  // Only once some ticket links an asset, so companies that don't track
  // equipment see no change.
  const showAssetColumn = activeCategory === "maintenance" && maintenanceRequests.some((t) => t.asset);

  const columns = [
    { id: "ticket_number", label: "#" },
    { id: "title", label: "Title" },
    { id: "status", label: "Status" },
    { id: "priority", label: "Priority" },
    { id: "assigned_to", label: "Assigned To" },
    ...((isManager || availableStores.length > 1) ? [{ id: "store", label: "Store" }] : []),
    { id: "issue_type", label: "Issue Type" },
    ...(showAssetColumn ? [{ id: "asset", label: "Asset" }] : []),
    ...(activeCategory === "menu" ? [{ id: "go_live_date", label: "Go Live" }] : []),
    // Menu tickets never have a vendor visit, and their final states are
    // Live or Canceled, so "Closed" reads better than "Resolved".
    ...(activeCategory === "menu" ? [] : [{ id: "scheduled", label: "Scheduled", sortable: false }]),
    { id: "images", label: "Attachments", sortable: false },
    { id: "date_reported", label: "Reported" },
    { id: "date_resolved", label: activeCategory === "menu" ? "Closed" : "Resolved" },
    { id: "submitter", label: "Submitter" },
  ];

  const ticketActions = (
            <div className="flex items-center gap-3">
              {isAdmin && accessibleCategories.length > 0 && (
                <Tooltip title="Ticket assignment settings" arrow>
                  <IconButton
                    onClick={() => setAssignmentSettingsOpen(true)}
                    size="small"
                    sx={{
                      color: "#475569",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      width: 40,
                      height: 40,
                      "&:hover": {
                        backgroundColor: "#f8fafc",
                        borderColor: "#cbd5e1",
                      },
                    }}
                  >
                    <SettingsOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <button
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-violet-700 text-violet-700 hover:bg-violet-50 text-sm font-medium"
                onClick={() => {
                  setRecurringDrawerOpen(true);
                  fetchRecurringTemplates();
                }}
              >
                <RepeatIcon fontSize="small" />
                Manage Recurring
              </button>
              <button
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-sm font-medium"
                onClick={handleNewTicket}
              >
                <AddIcon fontSize="small" />
                New Ticket
              </button>
            </div>
  );

  return (
    <div className="w-full min-h-screen bg-white">
      {showLightbox && selectedImage && (
        <div className="absolute w-screen h-screen z-50">
          <Lightbox
            small={selectedImage}
            medium={selectedImage}
            hideDownload={true}
            hideZoom={true}
            onClose={() => setShowLightbox(false)}
          />
        </div>
      )}
      {showVideoModal && selectedVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setShowVideoModal(false)}
        >
          <div
            className="relative max-w-3xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute -top-10 right-0 text-white hover:text-slate-300"
              onClick={() => setShowVideoModal(false)}
            >
              <CloseIcon />
            </button>
            <video
              controls
              autoPlay
              src={selectedVideo}
              className="w-full rounded-lg"
              style={{ maxHeight: "80vh" }}
            />
          </div>
        </div>
      )}
      {/* Header */}
      <div className="my-5">
      <OperationsPageHeader
        title={isMultiCategory ? "Tickets" : (CATEGORY_CONFIG[activeCategory]?.label || "Maintenance")}
        scope="company"
        homeStore={propsHomeStore}
        homeCompany={propsHomeCompany}
        storeOptions={storeOptions}
        storeNames={storeNames}
        change_home_store={props.change_home_store}
      >
        {/* Category tabs (only when multiple categories enabled) */}
        {isMultiCategory && (
          <SubTabs
            className="mt-4"
            tabs={accessibleCategories.map((cat) => ({
              key: cat.value,
              label: cat.label,
              icon: cat.icon,
            }))}
            activeTab={activeCategory}
            onChange={(value) => {
              setActiveCategory(value);
              setIssueTypeFilter([]);
              // Priority labels are per category too; a value picked under the
              // old category's wording must not keep filtering the new one.
              setPriorityFilter([]);
              setPage(0);
            }}
            rightSlot={view === "tickets" ? ticketActions : null}
          />
        )}
        {!isMultiCategory && view === "tickets" && (
          <div className="mt-4 flex items-center justify-end">{ticketActions}</div>
        )}

        {showAssetsToggle && (
          <div
            role="tablist"
            aria-label="Maintenance view"
            className="mt-4 inline-flex p-1 gap-1 rounded-[10px] bg-slate-100"
          >
            {[
              { key: "tickets", label: "Tickets" },
              { key: "assets", label: "Assets" },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                role="tab"
                aria-selected={view === opt.key}
                onClick={() => setView(opt.key)}
                className={`h-[34px] px-4 rounded-[7px] text-[13px] ${
                  view === opt.key
                    ? "bg-white text-slate-900 font-semibold shadow-sm"
                    : "text-slate-600 font-medium hover:text-slate-900"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {ticketOptionsStatus === "error" && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 flex items-center justify-between gap-2">
            <span>Couldn't load this company's ticket options, so priority labels are hidden.</span>
            <button type="button" className="underline font-medium" onClick={retryTicketOptions}>
              Retry
            </button>
          </div>
        )}

        {/* Filters */}
        {view === "tickets" && (
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          {/* Status filter */}
          <div className="flex-1 min-w-[140px]">
            <Select
              multiple
              value={statusFilter}
              onChange={handleMultiSelectChange(setStatusFilter, filterStatusOptions)}
              renderValue={() => {
                if (statusFilter.length === openStatusValues.length && openStatusValues.every((s) => statusFilter.includes(s))) return "Open";
                return renderMultiSelectValue(statusFilter, filterStatusOptions, "Statuses");
              }}
              size="small"
              displayEmpty
              sx={selectSx}
              className="w-full"
            >
              <MenuItem value="all">
                <Checkbox
                  checked={statusFilter.length === filterStatusOptions.length}
                  sx={{ color: "#6d28d9", "&.Mui-checked": { color: "#6d28d9" } }}
                />
                <ListItemText
                  primary="All Statuses"
                  primaryTypographyProps={{ style: { fontWeight: "bold" } }}
                />
              </MenuItem>
              {filterStatusOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Checkbox
                    checked={statusFilter.includes(opt.value)}
                    sx={{ color: "#6d28d9", "&.Mui-checked": { color: "#6d28d9" } }}
                  />
                  <ListItemText primary={opt.label} />
                </MenuItem>
              ))}
            </Select>
          </div>

          {/* Priority filter */}
          <div className="flex-1 min-w-[140px]">
            <Select
              multiple
              value={priorityFilter}
              disabled={ticketOptionsStatus !== "ready"}
              onChange={handleMultiSelectChange(setPriorityFilter, priorityOptions)}
              renderValue={() => renderMultiSelectValue(priorityFilter, priorityOptions, "Priorities")}
              size="small"
              displayEmpty
              sx={selectSx}
              className="w-full"
            >
              <MenuItem value="all">
                <Checkbox
                  checked={priorityFilter.length === priorityOptions.length}
                  sx={{ color: "#6d28d9", "&.Mui-checked": { color: "#6d28d9" } }}
                />
                <ListItemText
                  primary="All Priorities"
                  primaryTypographyProps={{ style: { fontWeight: "bold" } }}
                />
              </MenuItem>
              {priorityOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Checkbox
                    checked={priorityFilter.includes(opt.value)}
                    sx={{ color: "#6d28d9", "&.Mui-checked": { color: "#6d28d9" } }}
                  />
                  <ListItemText primary={opt.label} />
                </MenuItem>
              ))}
            </Select>
          </div>

          {/* Issue Type filter */}
          <div className="flex-1 min-w-[140px]">
            <Select
              multiple
              value={issueTypeFilter}
              onChange={handleMultiSelectChange(setIssueTypeFilter, issueTypeOptions)}
              renderValue={() => renderMultiSelectValue(issueTypeFilter, issueTypeOptions, "Issue Types")}
              size="small"
              displayEmpty
              sx={selectSx}
              className="w-full"
            >
              <MenuItem value="all">
                <Checkbox
                  checked={issueTypeFilter.length === issueTypeOptions.length}
                  sx={{ color: "#6d28d9", "&.Mui-checked": { color: "#6d28d9" } }}
                />
                <ListItemText
                  primary="All Issue Types"
                  primaryTypographyProps={{ style: { fontWeight: "bold" } }}
                />
              </MenuItem>
              {issueTypeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Checkbox
                    checked={issueTypeFilter.includes(opt.value)}
                    sx={{ color: "#6d28d9", "&.Mui-checked": { color: "#6d28d9" } }}
                  />
                  <ListItemText primary={opt.label} />
                </MenuItem>
              ))}
            </Select>
          </div>

          {/* Store filter (managers always, employees with multiple stores) */}
          {(isManager || availableStores.length > 1) && (
            <div className="flex-1 min-w-[140px]">
              <Select
                multiple
                value={storeFilter}
                onChange={(e) => {
                  const { value } = e.target;
                  if (value.includes("all")) {
                    setStoreFilter((prev) =>
                      prev.length === availableStores.length
                        ? []
                        : availableStores.map((s) => s.id)
                    );
                    return;
                  }
                  setStoreFilter(value);
                }}
                renderValue={() => {
                  if (storeFilter.length === 0 || storeFilter.length === availableStores.length) return "All Stores";
                  const names = availableStores
                    .filter((s) => storeFilter.includes(s.id))
                    .map((s) => s.store_name);
                  if (names.length <= 2) return names.join(", ");
                  return `${names[0]}, +${names.length - 1} more`;
                }}
                size="small"
                displayEmpty
                sx={selectSx}
                className="w-full"
              >
                <MenuItem value="all">
                  <Checkbox
                    checked={storeFilter.length === availableStores.length}
                    sx={{ color: "#6d28d9", "&.Mui-checked": { color: "#6d28d9" } }}
                  />
                  <ListItemText
                    primary="All Stores"
                    primaryTypographyProps={{ style: { fontWeight: "bold" } }}
                  />
                </MenuItem>
                {availableStores.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    <Checkbox
                      checked={storeFilter.includes(s.id)}
                      sx={{ color: "#6d28d9", "&.Mui-checked": { color: "#6d28d9" } }}
                    />
                    <ListItemText primary={s.store_name} />
                  </MenuItem>
                ))}
              </Select>
            </div>
          )}

          {/* Assigned To filter */}
          <div className="flex-1 min-w-[140px]">
            <Select
              multiple
              value={assignedToFilter}
              onChange={(e) => {
                const { value } = e.target;
                if (value.includes("all")) {
                  setAssignedToFilter((prev) =>
                    prev.length === availableAssignees.length
                      ? []
                      : availableAssignees.map((a) => a.id)
                  );
                } else {
                  setAssignedToFilter(value);
                }
                setPage(0);
              }}
              renderValue={() => {
                if (assignedToFilter.length === 0) return "All Assignees";
                const names = availableAssignees
                  .filter((a) => assignedToFilter.includes(a.id))
                  .map((a) => a.name);
                if (names.length === 0) return "All Assignees";
                if (names.length <= 1) return names.join(", ");
                return `${names[0]}, +${names.length - 1} more`;
              }}
              size="small"
              displayEmpty
              sx={selectSx}
              className="w-full"
            >
              <MenuItem value="all">
                <Checkbox
                  checked={assignedToFilter.length === availableAssignees.length && availableAssignees.length > 0}
                  sx={{ color: "#6d28d9", "&.Mui-checked": { color: "#6d28d9" } }}
                />
                <ListItemText
                  primary="All Assignees"
                  primaryTypographyProps={{ style: { fontWeight: "bold" } }}
                />
              </MenuItem>
              {availableAssignees.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  <Checkbox
                    checked={assignedToFilter.includes(a.id)}
                    sx={{ color: "#6d28d9", "&.Mui-checked": { color: "#6d28d9" } }}
                  />
                  <ListItemText primary={a.name} />
                </MenuItem>
              ))}
            </Select>
          </div>

          {/* Submitter filter */}
          <div className="flex-1 min-w-[140px]">
            <Select
              multiple
              value={submitterFilter}
              onChange={(e) => {
                const { value } = e.target;
                if (value.includes("all")) {
                  setSubmitterFilter((prev) =>
                    prev.length === availableSubmitters.length
                      ? []
                      : availableSubmitters.map((s) => s.id)
                  );
                } else {
                  setSubmitterFilter(value);
                }
                setPage(0);
              }}
              renderValue={() => {
                if (submitterFilter.length === 0) return "All Submitters";
                const names = availableSubmitters
                  .filter((s) => submitterFilter.includes(s.id))
                  .map((s) => s.name);
                if (names.length === 0) return "All Submitters";
                if (names.length <= 1) return names.join(", ");
                return `${names[0]}, +${names.length - 1} more`;
              }}
              size="small"
              displayEmpty
              sx={selectSx}
              className="w-full"
            >
              <MenuItem value="all">
                <Checkbox
                  checked={submitterFilter.length === availableSubmitters.length && availableSubmitters.length > 0}
                  sx={{ color: "#6d28d9", "&.Mui-checked": { color: "#6d28d9" } }}
                />
                <ListItemText
                  primary="All Submitters"
                  primaryTypographyProps={{ style: { fontWeight: "bold" } }}
                />
              </MenuItem>
              {availableSubmitters.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  <Checkbox
                    checked={submitterFilter.includes(s.id)}
                    sx={{ color: "#6d28d9", "&.Mui-checked": { color: "#6d28d9" } }}
                  />
                  <ListItemText primary={s.name} />
                </MenuItem>
              ))}
            </Select>
          </div>

          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <TextField
              fullWidth
              placeholder="Search tickets..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(0);
              }}
              size="small"
              InputProps={{
                startAdornment: (
                  <SearchIcon
                    sx={{ mr: 1, color: "text.secondary", fontSize: 20 }}
                  />
                ),
                endAdornment: searchQuery && (
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery("")}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "32px",
                  backgroundColor: "white",
                  borderRadius: "8px",
                  fontSize: "13px",
                  "&.Mui-focused fieldset": {
                    borderColor: "#6d28d9",
                  },
                },
              }}
            />
          </div>
        </div>
        )}
      </OperationsPageHeader>
      </div>

      {/* Content */}
      <div className="mx-10 pb-20">
        {view === "assets" ? (
          <AssetsView
            homeStore={homeStore}
            stores={assetStores}
            onOpenTicket={(ticket) => handleRowClick(ticket)}
            onNewTicketForAsset={handleNewTicketForAsset}
            refreshKey={assetRefreshKey}
          />
        ) : isLoading ? (
          <div className="flex items-center justify-center py-60">
            <LoadSpinner />
          </div>
        ) : sortedData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-500">
            {(() => { const Icon = CATEGORY_ICON_MAP[activeCategory] || HandymanIcon; return <Icon sx={{ fontSize: 48, color: "#cbd5e1", mb: 2 }} />; })()}
            <p className="text-lg font-medium text-slate-400">
              No {CATEGORY_CONFIG[activeCategory]?.label || "Maintenance"} tickets found
            </p>
            <p className="text-sm text-slate-400 mt-1">
              {maintenanceRequests.length > 0
                ? "Try adjusting your filters"
                : "Create a new ticket to get started"}
            </p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-[10px] border border-slate-200 overflow-hidden">
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table
                  stickyHeader
                  size="small"
                  // The Asset column adds its own width rather than taking it from Title.
                  sx={{ minWidth: 1600 + (showAssetColumn ? ASSET_COLUMN_WIDTH : 0) }}
                >
                  <TableHead>
                    <TableRow>
                      {columns.map((col) => (
                        <TableCell
                          key={col.id}
                          sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#334155", whiteSpace: "nowrap", borderBottom: "1px solid #e2e8f0" }}
                        >
                          {col.sortable === false ? (
                            col.label
                          ) : (
                            <TableSortLabel
                              active={orderBy === col.id}
                              direction={orderBy === col.id ? order : "asc"}
                              onClick={() => handleSort(col.id)}
                            >
                              {col.label}
                            </TableSortLabel>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.map((ticket) => (
                      <TableRow
                        key={ticket.uuid}
                        hover
                        onClick={() => handleRowClick(ticket)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell sx={{ fontSize: "0.8125rem", whiteSpace: "nowrap", color: "#64748b", fontVariantNumeric: "tabular-nums" }}>
                          {ticket.ticket_number != null ? `#${ticket.ticket_number}` : "—"}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.8125rem", minWidth: 260, maxWidth: 350, overflow: "hidden", wordBreak: "break-word" }}>
                          <div>
                            <div className="flex items-center gap-2.5">
                              <p className="font-semibold text-slate-900 text-xs">
                                {ticket.title}
                              </p>
                              <span className="flex items-center gap-2">
                                <span onClick={(e) => { e.stopPropagation(); handleRowClick(ticket, "details"); }}>
                                  <MaintenanceHistoryTooltip
                                    ticketUuid={ticket.uuid}
                                    homeStore={homeStore}
                                    updatedAt={ticket.updated_at}
                                  />
                                </span>
                                <span onClick={(e) => { e.stopPropagation(); handleRowClick(ticket, "conversation"); }}>
                                  <MaintenanceCommentTooltip
                                    ticketUuid={ticket.uuid}
                                    homeStore={homeStore}
                                    commentCount={ticket.comment_count || 0}
                                  />
                                </span>
                              </span>
                              {ticket.recurring_template && (
                                <Tooltip title="Recurring ticket" arrow>
                                  <RepeatIcon sx={{ fontSize: 15, color: "#94a3b8" }} />
                                </Tooltip>
                              )}
                            </div>
                            {ticket.description && (
                              <p className="text-xs text-slate-500 mt-0.5">
                                {ticket.description}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.8125rem", whiteSpace: "nowrap" }}>
                          <StatusBadge status={ticket.status} approvalStatus={ticket.approval_status} label={statusLabelFor(ticket.status)} />
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.8125rem", whiteSpace: "nowrap" }}>
                          {ticketOptionsStatus === "ready" ? (
                            <PriorityBadge priority={ticket.priority} label={priorityLabelFor(ticket.priority)} />
                          ) : (
                            <span className="text-[13px] text-slate-400" title="Loading priority labels">…</span>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.8125rem", whiteSpace: "nowrap" }}>
                          {ticket.assignees?.length ? (
                            <div className="flex flex-col gap-1">
                              {ticket.assignees.map((a) => (
                                <div key={a.id || a.full_name} className="flex items-center gap-1.5">
                                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-200 text-slate-700 flex-shrink-0" style={{ fontSize: "9px", fontWeight: 600, lineHeight: 1 }}>
                                    {a.initials}
                                  </span>
                                  <span className="text-slate-900">{formatName(a.full_name)}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-400">Unassigned</span>
                          )}
                        </TableCell>
                        {(isManager || availableStores.length > 1) && (
                          <TableCell sx={{ fontSize: "0.8125rem", whiteSpace: "nowrap", color: "#334155" }}>
                            {ticket.store?.name}
                          </TableCell>
                        )}
                        <TableCell sx={{ fontSize: "0.8125rem", whiteSpace: "nowrap" }}>
                          <IssueTypeBadge issueType={ticket.issue_type} />
                        </TableCell>
                        {showAssetColumn && (
                          <TableCell
                            sx={{
                              fontSize: "0.8125rem",
                              whiteSpace: "nowrap",
                              color: "#334155",
                              maxWidth: ASSET_COLUMN_WIDTH,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={ticket.asset ? `${ticket.asset.code} ${ticket.asset.name}` : undefined}
                          >
                            {ticket.asset ? (
                              <span>
                                <span className="text-slate-500 tabular-nums">{ticket.asset.code}</span>{" "}
                                {ticket.asset.name}
                              </span>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </TableCell>
                        )}
                        {activeCategory === "menu" && (
                          <TableCell sx={{ fontSize: "0.8125rem", color: "#64748b", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                            {ticket.menu_details?.go_live_date || <span className="text-slate-300">—</span>}
                          </TableCell>
                        )}
                        {activeCategory !== "menu" && (
                        <TableCell sx={{ fontSize: "0.8125rem", color: "#0f172a" }}>
                          {["scheduled", "completed", "closed"].includes(ticket.status) && (ticket.vendor || ticket.scheduled_time) ? (
                            <div className="space-y-0.5">
                              {ticket.vendor && (
                                <p>{ticket.vendor}</p>
                              )}
                              {ticket.scheduled_time && (
                                <p className="text-xs text-slate-500">
                                  {new Date(ticket.scheduled_time).toLocaleString(undefined, {
                                    month: "short",
                                    day: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </TableCell>
                        )}
                        <TableCell>
                          {ticket.file_uploads?.length > 0 && (
                            <div className="flex items-center gap-1">
                              {ticket.file_uploads.slice(0, 3).map((file) => {
                                const kind = getFileKind(file);
                                return (
                                <Tooltip key={file.uuid} title={file.name || ""}>
                                <div
                                  className="w-8 h-8 rounded-md border border-slate-200 overflow-hidden flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!file.presigned_url) return;
                                    if (kind === "video") {
                                      setSelectedVideo(file.presigned_url);
                                      setShowVideoModal(true);
                                    } else if (kind === "image") {
                                      setSelectedImage(file.presigned_url);
                                      setShowLightbox(true);
                                    } else {
                                      window.open(file.presigned_url, "_blank", "noopener,noreferrer");
                                    }
                                  }}
                                >
                                  {!file.presigned_url ? (
                                    <div className="w-full h-full flex items-center justify-center bg-slate-50">
                                      <ImageIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                                    </div>
                                  ) : kind === "video" ? (
                                    <div className="w-full h-full flex items-center justify-center bg-slate-900 rounded">
                                      <PlayCircleOutlineIcon sx={{ color: "white", fontSize: 20 }} />
                                    </div>
                                  ) : kind === "image" ? (
                                    <img
                                      src={file.presigned_url}
                                      alt=""
                                      className="w-full h-full object-cover"
                                    />
                                  ) : kind === "pdf" ? (
                                    <div className="w-full h-full flex items-center justify-center bg-slate-50">
                                      <PictureAsPdfIcon sx={{ fontSize: 22, color: "#dc2626" }} />
                                    </div>
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-slate-50">
                                      <InsertDriveFileIcon sx={{ fontSize: 20, color: "#94a3b8" }} />
                                    </div>
                                  )}
                                </div>
                                </Tooltip>
                                );
                              })}
                              {ticket.file_uploads.length > 3 && (
                                <span className="text-xs text-slate-500">
                                  +{ticket.file_uploads.length - 3}
                                </span>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.8125rem", color: "#64748b", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                          {ticket.date_reported}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.8125rem", color: "#64748b", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                          {ticket.date_resolved || <span className="text-slate-300">—</span>}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.8125rem", whiteSpace: "nowrap", color: "#334155" }}>
                          {formatName(ticket.submitter?.full_name)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={sortedData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
      </div>

      {/* Drawer */}
      <MaintenanceTicketDrawer
        isOpen={drawerOpen}
        initialTab={initialTab}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedTicket(null);
          setDrawerInitialAsset(null);
          setInitialTab("details");
        }}
        ticket={selectedTicket}
        initialAsset={drawerInitialAsset}
        category={activeCategory}
        onCommentPosted={() => fetchMaintenanceRequests(false)}
        onSubmit={selectedTicket ? handleUpdateTicket : handleCreateTicket}
        onDelete={handleDeleteTicket}
        onDecideApproval={handleDecideApproval}
        permissionRole={permissionRole}
        permissionOverrides={permissionOverrides}
        homeCompany={homeCompany}
        homeStore={homeStore}
        storeNames={availableStores}
        createStoreNames={createStores}
        employees={employees}
        approvers={approvers}
        onStoreChange={fetchTicketPeople}
        issueTypeOptions={issueTypeOptions}
        priorityOptions={priorityOptions}
        statusOptions={statusOptions}
        menuOptions={menuOptions}
        optionsStatus={ticketOptionsStatus}
        onRetryOptions={retryTicketOptions}
      />

      {/* Recurring Management Drawer */}
      <RecurringManagementDrawer
        isOpen={recurringDrawerOpen}
        onClose={() => setRecurringDrawerOpen(false)}
        templates={recurringTemplates}
        isLoading={recurringTemplatesLoading}
        onRefresh={fetchRecurringTemplates}
        category={activeCategory}
        homeStore={homeStore}
        storeNames={availableStores}
        employees={employees}
        onStoreChange={fetchEmployees}
        permissionRole={permissionRole}
        permissionOverrides={permissionOverrides}
        issueTypeOptions={issueTypeOptions}
        priorityOptions={priorityOptions}
        optionsStatus={ticketOptionsStatus}
        onRetryOptions={retryTicketOptions}
      />

      {/* Ticket Assignment Settings (admin) */}
      <TicketAssignmentSettingsModal
        open={assignmentSettingsOpen}
        onClose={() => setAssignmentSettingsOpen(false)}
        storeId={homeStore}
        accessibleCategories={accessibleCategories}
        onSaved={() => {
          if (selectedTicket?.store?.id) {
            fetchTicketPeople(selectedTicket.store.id);
          } else {
            fetchTicketPeople();
          }
        }}
      />
    </div>
  );
}

export default MaintenanceDisplay;
